import { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Gavel, Loader2, Trophy, HandMetal, LogIn, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { fmtDateTime, timeAgo } from "@/lib/format";
import { AUCTION_TYPE_LABELS } from "@/components/AuctionFields";

const money = (v, cur = "INR") => (v == null ? "—" : `${cur === "INR" ? "₹" : cur + " "}${Number(v).toLocaleString("en-IN")}`);

function Countdown({ endAt }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  const s = Math.max(0, Math.floor((new Date(endAt).getTime() - now) / 1000));
  const h = String(Math.floor(s / 3600)).padStart(2, "0"), m = String(Math.floor((s % 3600) / 60)).padStart(2, "0"), sec = String(s % 60).padStart(2, "0");
  return <span className="font-mono text-sm font-semibold" data-testid="seller-auction-countdown">{s === 0 ? "Auction Closed" : `${h}:${m}:${sec} remaining`}</span>;
}

function ServerTimer({ label, targetAt, serverTime, testid }) {
  const [now, setNow] = useState(Date.now());
  const sync = useRef(null);
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  useEffect(() => { if (serverTime) sync.current = { s: new Date(serverTime).getTime(), l: Date.now() }; }, [serverTime]);
  const base = sync.current ? sync.current.s + (now - sync.current.l) : now;
  const s = Math.max(0, Math.floor((new Date(targetAt).getTime() - base) / 1000));
  const h = String(Math.floor(s / 3600)).padStart(2, "0"), m = String(Math.floor((s % 3600) / 60)).padStart(2, "0"), sec = String(s % 60).padStart(2, "0");
  return (
    <span className="flex items-center gap-2" data-testid={testid}>
      <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
      <span className="font-mono text-sm font-bold tabular-nums" data-testid={`${testid}-value`}>{h}:{m}:{sec}</span>
    </span>
  );
}

export default function SellerAuctionPanel({ inviteId }) {
  const [state, setState] = useState(null);
  const [missing, setMissing] = useState(false);
  const [bid, setBid] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get(`/seller/enquiries/${inviteId}/auction`)
      .then((r) => { setState(r.data); setMissing(false); })
      .catch((e) => { if (e?.response?.status === 404) setMissing(true); });
  }, [inviteId]);
  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);

  if (missing || !state) return null;
  const { auction: a, participant, eligible, event_expired } = state;
  const terminal = ["closed", "cancelled", "no_winner", "award_pending", "awarded"].includes(a.status);
  const active = participant?.status === "active";
  const dropped = participant?.status === "dropped";
  const canAct = !terminal && !event_expired && active;
  const actedThisRound = state.my_actions?.some((x) => ["STAY_IN", "DROP_OUT"].includes(x.action_type) && x.metadata?.round === state.current_round);

  const act = async (fn, success) => {
    setSaving(true);
    try { await fn(); toast.success(success); setBid(""); load(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  return (
    <div className="rounded-lg border border-slate-300 bg-card" data-testid="seller-auction-panel">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b px-5 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <Gavel className="h-4 w-4 text-slate-500" />
          <h2 className="font-heading text-base font-semibold">{a.name}</h2>
          <span className="rounded-full border border-slate-200 bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600">{AUCTION_TYPE_LABELS[a.auction_type]}</span>
          <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-xs font-medium text-slate-600" data-testid="seller-auction-round">Round {a.round_number || 1}</span>
          <span data-testid="seller-auction-status" className={`rounded-full border px-2.5 py-0.5 text-xs font-medium capitalize ${terminal ? "bg-slate-100 text-slate-600 border-slate-200" : "bg-[#DCFCE7] text-[#166534] border-[#BBF7D0]"}`}>{a.status.replace(/_/g, " ")}</span>
        </div>
        {!terminal && a.status === "scheduled" ? (
          <ServerTimer label="Auction Starts In" targetAt={a.start_at} serverTime={state.server_time} testid="seller-auction-starts-in" />
        ) : !terminal && a.auction_type === "japanese" ? (
          <ServerTimer label="Response Time Remaining" targetAt={state.current_round_ends_at || a.end_at} serverTime={state.server_time} testid="seller-round-time-remaining" />
        ) : (!terminal && <Countdown endAt={a.end_at} />)}
      </div>

      <div className="px-5 py-4 space-y-4">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4 text-sm">
          <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Start</p><p className="mt-1 text-xs">{fmtDateTime(a.start_at)}</p></div>
          <div><p className="text-xs uppercase tracking-wide text-muted-foreground">End</p><p className="mt-1 text-xs">{fmtDateTime(a.end_at)}</p></div>
          {a.auction_type !== "japanese" && <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Active Participants</p><p className="mt-1 font-mono text-sm">{state.active_count}</p></div>}
          {a.auction_type === "english" && (
            <div><p className="text-xs uppercase tracking-wide text-muted-foreground">{a.bid_visibility === "rank" ? "Your Rank" : "Best Bid"}</p>
              <p className="mt-1 font-mono text-sm font-semibold" data-testid="seller-visibility-info">
                {a.bid_visibility === "rank" && (state.my_rank ? `#${state.my_rank}` : "No bid yet")}
                {a.bid_visibility === "price" && money(state.best_bid, a.currency)}
                {a.bid_visibility === "rank_price" && `${state.my_rank ? `#${state.my_rank}` : "—"} · Best ${money(state.best_bid, a.currency)}`}
              </p>
            </div>
          )}
        </div>

        {event_expired && !terminal && <p className="rounded-md border border-[#FECACA] bg-[#FEE2E2]/40 px-4 py-2.5 text-sm text-[#991B1B]" data-testid="event-expired-note">This event has closed and no further responses can be submitted.</p>}
        {dropped && <p className="rounded-md border border-[#FECACA] bg-[#FEE2E2]/40 px-4 py-2.5 text-sm text-[#991B1B]" data-testid="dropped-note">You dropped out of this auction. This decision is permanent.</p>}
        {Array.isArray(state.scope_items) && state.scope_items.length > 0 && (
          <div className="rounded-lg border px-4 py-3" data-testid="seller-auction-scope">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Auction Items — {state.scope_items.length} Item{state.scope_items.length === 1 ? "" : "s"}</p>
            <div className="mt-2 space-y-1.5">
              {state.scope_items.map((it, n) => (
                <div key={it.material_code} className="flex flex-wrap items-baseline gap-x-3 text-sm" data-testid={`seller-scope-item-${it.material_code}`}>
                  <span className="font-mono text-xs font-semibold">{n + 1}. {it.material_code}</span>
                  <span className="text-muted-foreground">{it.description}</span>
                  <span className="font-mono text-xs">{it.quantity} {it.uom}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {state.result === "won" && (a.auction_type === "japanese" ? (
          <p className="rounded-md border border-[#BBF7D0] bg-[#DCFCE7]/40 px-4 py-2.5 text-sm font-medium text-[#166534]" data-testid="won-banner"><Trophy className="mr-1.5 inline h-4 w-4" /> AUCTION WON — Your winning price: {money(state.final_price, a.currency)}. You accepted the last price level of {money(state.final_price, a.currency)} before exiting at the next price level.</p>
        ) : (
          <p className="rounded-md border border-[#BBF7D0] bg-[#DCFCE7]/40 px-4 py-2.5 text-sm font-medium text-[#166534]" data-testid="won-banner"><Trophy className="mr-1.5 inline h-4 w-4" /> You won this auction{state.final_price ? ` at ${money(state.final_price, a.currency)}` : ""}.</p>
        ))}
        {state.result === "closed" && a.auction_type === "japanese" && <p className="rounded-md border bg-slate-50 px-4 py-2.5 text-sm text-slate-600" data-testid="closed-banner">AUCTION CLOSED — Your participation has ended.</p>}
        {state.result === "lost" && <p className="rounded-md border bg-slate-50 px-4 py-2.5 text-sm text-slate-600" data-testid="lost-banner">The auction has closed. You were not selected for this event.</p>}
        {state.result === "leading" && <p className="rounded-md border border-[#BFDBFE] bg-[#DBEAFE]/40 px-4 py-2.5 text-sm text-[#1E40AF]" data-testid="leading-banner">Auction closed — you are the leading seller. The buyer retains final award authority.</p>}

        {!participant?.status || participant.status === "invited" ? (
          !terminal && !event_expired && (
            a.auction_type === "japanese" && a.status !== "scheduled" ? (
              <p className="rounded-md border border-[#FECACA] bg-[#FEE2E2]/40 px-4 py-2.5 text-sm text-[#991B1B]" data-testid="joining-closed-note">Joining closed — this auction required joining before the start time. No late entry is permitted.</p>
            ) : eligible ? (
              <div>
                {a.auction_type === "japanese" && <p className="mb-2 text-xs font-medium text-amber-700" data-testid="join-deadline-note">You must join before the auction starts — late entry is not permitted.</p>}
                <Button data-testid="join-auction-btn" disabled={saving} onClick={() => act(() => api.post(`/seller/enquiries/${inviteId}/auction/join`), "Joined the auction")} className="bg-slate-900 hover:bg-slate-800">
                  {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <LogIn className="mr-1.5 h-4 w-4" />} Join Auction
                </Button>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground" data-testid="join-requires-response">Submit your event response first to become eligible for the auction.</p>
            )
          )
        ) : null}

        {canAct && a.auction_type === "english" && (
          <div className="rounded-md border p-4" data-testid="english-bid-box">
            <Label>Enter Bid ({a.currency}) — must beat current best by at least {money(a.bid_decrement, a.currency)}</Label>
            <div className="mt-1.5 flex gap-2">
              <Input data-testid="bid-amount-input" type="number" min="1" step="any" className="max-w-56 font-mono" placeholder="e.g. 94000" value={bid} onChange={(e) => setBid(e.target.value)} />
              <Button data-testid="submit-bid-btn" disabled={saving || !bid}
                onClick={() => act(() => api.post(`/seller/enquiries/${inviteId}/auction/bid`, { amount: Number(bid) }), "Bid submitted")}
                className="bg-slate-900 hover:bg-slate-800">Submit Bid</Button>
            </div>
            {state.my_best && <p className="mt-2 text-xs text-muted-foreground">Your current best bid: {money(state.my_best, a.currency)}</p>}
          </div>
        )}

        {canAct && a.auction_type === "dutch" && (
          <div className="rounded-md border p-4 text-center" data-testid="dutch-accept-box">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Current Available Price</p>
            <p className="mt-1 font-heading text-3xl font-bold" data-testid="dutch-current-price">{money(state.current_price, a.currency)}</p>
            {state.seconds_to_next != null && <p className="mt-1 font-mono text-xs text-muted-foreground">Next increase in {state.seconds_to_next}s</p>}
            <Button data-testid="accept-price-btn" disabled={saving} className="mt-3 bg-[#166534] hover:bg-[#14532D]"
              onClick={() => act(() => api.post(`/seller/enquiries/${inviteId}/auction/accept`), "Price accepted — auction closed")}>
              <HandMetal className="mr-1.5 h-4 w-4" /> Accept Current Price
            </Button>
          </div>
        )}

        {canAct && a.auction_type === "japanese" && (
          <div className="rounded-md border p-4 text-center" data-testid="japanese-round-box">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Round {state.current_round} — Current Price</p>
            {state.current_round_ends_at && (
              <div className="mt-2 flex justify-center">
                <ServerTimer label="Response Time Remaining" targetAt={state.current_round_ends_at} serverTime={state.server_time} testid="seller-round-timer" />
              </div>
            )}
            <p className="mt-1 font-heading text-3xl font-bold" data-testid="japanese-current-price">{money(state.current_price, a.currency)}</p>
            {actedThisRound ? (
              <p className="mt-3 text-sm text-muted-foreground" data-testid="round-acted-note">Response recorded for this round. Waiting for the next round…</p>
            ) : (
              <div className="mt-3 flex justify-center gap-3">
                <Button data-testid="stay-in-btn" disabled={saving} className="bg-[#166534] hover:bg-[#14532D]"
                  onClick={() => act(() => api.post(`/seller/enquiries/${inviteId}/auction/respond`, { action: "stay_in" }), "Stayed in for this round")}>
                  Stay In
                </Button>
                <Button data-testid="drop-out-btn" variant="outline" disabled={saving} className="border-red-200 text-red-700 hover:bg-red-50"
                  onClick={() => act(() => api.post(`/seller/enquiries/${inviteId}/auction/respond`, { action: "drop_out" }), "You dropped out of the auction")}>
                  <LogOut className="mr-1.5 h-4 w-4" /> Drop Out
                </Button>
              </div>
            )}
          </div>
        )}

        {(state.my_bids?.length > 0 || state.my_actions?.length > 0) && (
          <div>
            <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Your Activity</h3>
            <div className="max-h-40 space-y-1 overflow-y-auto" data-testid="seller-auction-history">
              {state.my_bids.map((b) => (
                <div key={b.id} className="flex justify-between text-sm">
                  <span>{b.bid_type === "accept" ? "Accepted price" : "Your bid"}</span>
                  <span className="font-mono text-xs font-semibold">{money(b.bid_amount, a.currency)}</span>
                  <span className="text-xs text-muted-foreground">{timeAgo(b.created_at)}</span>
                </div>
              ))}
              {state.my_actions.filter((x) => ["STAY_IN", "DROP_OUT", "JOINED"].includes(x.action_type)).map((x) => (
                <div key={x.id} className="flex justify-between text-xs text-muted-foreground">
                  <span>{x.action_type.replace(/_/g, " ").toLowerCase()}{x.metadata?.round ? ` (round ${x.metadata.round})` : ""}</span>
                  <span>{timeAgo(x.created_at)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
